import { XMarkIcon } from "@heroicons/react/24/outline";
import Drawer from "@via/components/drawer.tsx";
import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";
import { useUpdateVoucherCode } from "@via/pages/account/admin/voucher-codes/voucher-codes.api.tsx";
import { classNames, formatDateString } from "@via/utils/helpers.tsx";
import { accessCodeOptions } from "@via/utils/form-validators.tsx";
import { VoucherCodeWithStatsRecord } from "common";
import dayjs from "dayjs";
import { type JSX, useEffect } from "react";
import { useForm } from "react-hook-form";

interface EditAccessCodeFormValues {
  expiresAt: string;
}

interface EditVoucherDrawerProps {
  voucher: VoucherCodeWithStatsRecord | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function EditVoucherDrawer({
  voucher,
  isOpen,
  onClose,
}: EditVoucherDrawerProps): JSX.Element {
  return (
    <Drawer
      id="edit-voucher"
      open={isOpen}
      setOpen={() => onClose()}
      showMinimizeButton={false}>
      {voucher ? (
        <EditVoucherForm voucher={voucher} onClose={onClose} />
      ) : (
        <></>
      )}
    </Drawer>
  );
}

function EditVoucherForm({
  voucher,
  onClose,
}: {
  voucher: VoucherCodeWithStatsRecord;
  onClose: () => void;
}): JSX.Element {
  const {
    mutate: updateVoucher,
    isPending: isSaving,
    isSuccess: isUpdateSuccess,
    isError: isUpdateError,
    error: updateError,
  } = useUpdateVoucherCode();

  const minExpiresAt = dayjs().add(1, "day");
  const voucherExpiry = dayjs(voucher.expiresAt);
  const initialExpiresAt = (
    voucherExpiry.isBefore(minExpiresAt, "day") ? minExpiresAt : voucherExpiry
  ).format("YYYY-MM-DD");

  const { control, handleSubmit, formState } =
    useForm<EditAccessCodeFormValues>({
      mode: "onChange",
      defaultValues: {
        expiresAt: initialExpiresAt,
      },
    });

  useEffect(() => {
    if (isUpdateSuccess) {
      onClose();
    }
  }, [isUpdateSuccess, onClose]);

  const onSubmit = (data: EditAccessCodeFormValues) => {
    updateVoucher({
      id: voucher.id,
      expiresAt: dayjs(data.expiresAt).endOf("day").toISOString(),
    });
  };

  const hasChanges = formState.isDirty;

  return (
    <form
      className="flex h-full flex-col"
      onSubmit={e => {
        e.preventDefault();
        void handleSubmit(onSubmit)(e);
      }}>
      <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold leading-6 text-gray-900">
            Edit Access Code
          </h2>
          <button
            type="button"
            className="rounded-md text-gray-900 hover:text-gray-600 focus:outline-none"
            onClick={onClose}
            aria-label="Close edit drawer">
            <span className="sr-only">Close</span>
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Update access code settings.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Code
            </label>
            <p className="mt-1 font-mono text-sm text-gray-900">
              {voucher.code}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Redeemed By
            </label>
            {voucher.redeemedBy !== null ? (
              <div className="mt-1 text-sm text-gray-600">
                <div>{voucher.redeemedByEmail || "Unknown"}</div>
                {voucher.redeemedAt && (
                  <div className="text-xs text-gray-400">
                    {formatDateString(voucher.redeemedAt, "MMM D, YYYY")}
                  </div>
                )}
              </div>
            ) : (
              <p className="mt-1 text-sm italic text-gray-400">Not redeemed</p>
            )}
          </div>

          <hr className="border-gray-200" />

          <FormInputComponent
            control={control}
            name="expiresAt"
            label="Expiration Date"
            type="date"
            validations={accessCodeOptions.expiresAt}
            showRequiredField
            min={minExpiresAt.format("YYYY-MM-DD")}
            max={dayjs().add(1, "year").format("YYYY-MM-DD")}
          />

          {voucher.isRevoked && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-sm font-medium text-red-800">
                This code has been revoked and cannot be redeemed.
              </p>
            </div>
          )}
        </div>
      </div>

      {isUpdateError && (
        <div className="mx-4 rounded-md border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-800">
            Failed to update access code.
            {updateError?.message && (
              <span className="text-red-600">{updateError.message}</span>
            )}
          </p>
        </div>
      )}

      <div className="flex flex-shrink-0 justify-end gap-3 border-t px-4 py-4">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSaving || !hasChanges}
          className={classNames(
            "inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm",
            isSaving || !hasChanges
              ? "bg-indigo-400 cursor-not-allowed"
              : "bg-indigo-600 hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          )}>
          <Spinner loading={isSaving}>Save Changes</Spinner>
        </button>
      </div>
    </form>
  );
}
